// src/components/Questionnaires/CompetencyProfile.js

import React, { useEffect, useState } from 'react';
import { Card, Form, Radio, Input, Button, Alert, Typography, message, Spin, Divider } from 'antd';
import moment from 'moment';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CompetencyProfile = ({ server_url, accessToken }) => {
    const [form] = Form.useForm();
    const [isEligible, setIsEligible] = useState(true);
    const [eligibilityMessage, setEligibilityMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [latestSubmission, setLatestSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch eligibility and latest submission on component mount
    useEffect(() => {
        const checkEligibility = async () => {
            try {
                // Check Eligibility
                const eligibilityResponse = await fetch(`${server_url}/questionnaires/competency-profile/eligibility`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${accessToken}` },
                });

                if (!eligibilityResponse.ok) {
                    throw new Error('Failed to check eligibility.');
                }

                const eligibilityData = await eligibilityResponse.json();
                setIsEligible(eligibilityData.eligible);
                setEligibilityMessage(eligibilityData.message);

                if (eligibilityData.eligible) {
                    // Fetch the latest submission
                    const latestResponse = await fetch(`${server_url}/questionnaires/competency-profile/latest`, {
                        method: 'GET',
                        headers: { Authorization: `Bearer ${accessToken}` },
                    });

                    if (!latestResponse.ok) {
                        throw new Error('Failed to fetch latest submission.');
                    }

                    const latestData = await latestResponse.json();
                    if (latestData.latest_submission) {
                        setLatestSubmission(latestData.latest_submission);
                        form.setFieldsValue(latestData.latest_submission.qna);
                    }
                }
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        checkEligibility();
    }, [server_url, accessToken, form]);

    // Handle form submission
    const handleSubmit = async (values) => {
        setIsSubmitting(true);
        try {
            const response = await fetch(`${server_url}/questionnaires/competency-profile/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    form_data: {
                        answers: values,
                    },
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to submit Competency Profile.');
            }

            const data = await response.json();
            message.success('Competency Profile submitted successfully!');
            setIsEligible(false);
            setEligibilityMessage(`Next eligible date: ${moment(data.next_eligible_date).format('YYYY-MM-DD')}`);
            // Optionally, you can reset the form or keep the latest submission displayed
        } catch (err) {
            console.error('Error submitting Competency Profile:', err);
            message.error(err.message || 'An error occurred while submitting the form.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <Spin tip="Loading..." />;
    }

    if (error) {
        return <Alert message={error} type="error" showIcon />;
    }

    return (
        <Card title="Student’s Competency Profile">
            {/* Eligibility Message */}
            <Alert
                message={eligibilityMessage}
                type={isEligible ? "success" : "warning"}
                showIcon
                style={{ marginBottom: 16 }}
            />

            {/* If Eligible, Show the Form */}
            {isEligible ? (
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={handleSubmit}
                    initialValues={latestSubmission ? latestSubmission.qna : {}}
                >
                    {/* 1. Oral Communication */}
                    <Title level={4}>1. Oral Communication</Title>
                    <Text italic>Objective: Assess the ability to articulate thoughts, express ideas, and engage in verbal communication effectively.</Text>
                    <Divider />

                    {/* Basic (B) */}
                    <Title level={5}>Basic (B)</Title>
                    <Form.Item
                        label="Can the student introduce themselves clearly in a sentence or two?"
                        name="Can the student introduce themselves clearly in a sentence or two?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student hesitate or struggle to find the right words while speaking?"
                        name="Does the student hesitate or struggle to find the right words while speaking?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student understand simple verbal instructions and respond accordingly?"
                        name="Can the student understand simple verbal instructions and respond accordingly?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Progressing (P) */}
                    <Title level={5}>Progressing (P)</Title>
                    <Form.Item
                        label="Can the student explain a concept or topic of their choice for 2-3 minutes?"
                        name="Can the student explain a concept or topic of their choice for 2-3 minutes?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student use appropriate vocabulary for the context?"
                        name="Does the student use appropriate vocabulary for the context?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="How frequently does the student make grammatical errors while speaking?"
                        name="How frequently does the student make grammatical errors while speaking?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Often">Often</Radio>
                            <Radio value="Sometimes">Sometimes</Radio>
                            <Radio value="Rarely">Rarely</Radio>
                            <Radio value="Never">Never</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student engage actively in group discussions?"
                        name="Does the student engage actively in group discussions?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Advanced (A) */}
                    <Title level={5}>Advanced (A)</Title>
                    <Form.Item
                        label="Can the student confidently give a short speech or presentation in front of a group?"
                        name="Can the student confidently give a short speech or presentation in front of a group?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student ask relevant questions during discussions to deepen understanding?"
                        name="Does the student ask relevant questions during discussions to deepen understanding?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="How effectively does the student adapt their tone and style based on the audience?"
                        name="How effectively does the student adapt their tone and style based on the audience?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Very Effectively">Very Effectively</Radio>
                            <Radio value="Effectively">Effectively</Radio>
                            <Radio value="Somewhat Effectively">Somewhat Effectively</Radio>
                            <Radio value="Not Effectively">Not Effectively</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Divider />

                    {/* 2. Digital Literacy */}
                    <Title level={4}>2. Digital Literacy</Title>
                    <Text italic>Objective: Evaluate the ability to use digital tools, understand basic technology concepts, and practice safe online behavior.</Text>
                    <Divider />

                    {/* Basic (B) */}
                    <Title level={5}>Basic (B)</Title>
                    <Form.Item
                        label="Can the student operate basic devices like smartphones, tablets, or laptops?"
                        name="Can the student operate basic devices like smartphones, tablets, or laptops?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student know how to create and save files on a computer?"
                        name="Does the student know how to create and save files on a computer?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student perform basic internet searches?"
                        name="Can the student perform basic internet searches?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Progressing (P) */}
                    <Title level={5}>Progressing (P)</Title>
                    <Form.Item
                        label="Can the student use common software like Word processors, presentation tools, or spreadsheets?"
                        name="Can the student use common software like Word processors, presentation tools, or spreadsheets?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student understand online safety practices (e.g., avoiding phishing, creating strong passwords)?"
                        name="Does the student understand online safety practices (e.g., avoiding phishing, creating strong passwords)?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student access and use educational platforms (e.g., Google Classroom, Moodle)?"
                        name="Can the student access and use educational platforms (e.g., Google Classroom, Moodle)?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Advanced (A) */}
                    <Title level={5}>Advanced (A)</Title>
                    <Form.Item
                        label="Can the student troubleshoot common device issues independently?"
                        name="Can the student troubleshoot common device issues independently?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student effectively collaborate with peers using online tools (e.g., Google Docs, Zoom)?"
                        name="Does the student effectively collaborate with peers using online tools (e.g., Google Docs, Zoom)?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student evaluate the credibility of online information and sources?"
                        name="Can the student evaluate the credibility of online information and sources?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Divider />

                    {/* 3. Written Communication */}
                    <Title level={4}>3. Written Communication</Title>
                    <Text italic>Objective: Assess the ability to express ideas clearly and effectively in written format.</Text>
                    <Divider />

                    {/* Basic (B) */}
                    <Title level={5}>Basic (B)</Title>
                    <Form.Item
                        label="Can the student write short, coherent sentences with proper punctuation?"
                        name="Can the student write short, coherent sentences with proper punctuation?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student struggle with basic grammar and spelling?"
                        name="Does the student struggle with basic grammar and spelling?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Often">Often</Radio>
                            <Radio value="Sometimes">Sometimes</Radio>
                            <Radio value="Rarely">Rarely</Radio>
                            <Radio value="Never">Never</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student write a simple paragraph on a given topic?"
                        name="Can the student write a simple paragraph on a given topic?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Progressing (P) */}
                    <Title level={5}>Progressing (P)</Title>
                    <Form.Item
                        label="Can the student write a structured essay with an introduction, body, and conclusion?"
                        name="Can the student write a structured essay with an introduction, body, and conclusion?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student vary sentence structures and use appropriate vocabulary?"
                        name="Does the student vary sentence structures and use appropriate vocabulary?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="How effectively does the student convey their main ideas in writing?"
                        name="How effectively does the student convey their main ideas in writing?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Very Effectively">Very Effectively</Radio>
                            <Radio value="Effectively">Effectively</Radio>
                            <Radio value="Somewhat Effectively">Somewhat Effectively</Radio>
                            <Radio value="Not Effectively">Not Effectively</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Advanced (A) */}
                    <Title level={5}>Advanced (A)</Title>
                    <Form.Item
                        label="Can the student write persuasive or creative pieces, such as speeches or stories?"
                        name="Can the student write persuasive or creative pieces, such as speeches or stories?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student revise and edit their work to improve quality?"
                        name="Does the student revise and edit their work to improve quality?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Frequently">Frequently</Radio>
                            <Radio value="Occasionally">Occasionally</Radio>
                            <Radio value="Rarely">Rarely</Radio>
                            <Radio value="Never">Never</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="How effectively does the student adapt their writing style for different purposes (e.g., formal reports vs. personal blogs)?"
                        name="How effectively does the student adapt their writing style for different purposes (e.g., formal reports vs. personal blogs)?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Very Effectively">Very Effectively</Radio>
                            <Radio value="Effectively">Effectively</Radio>
                            <Radio value="Somewhat Effectively">Somewhat Effectively</Radio>
                            <Radio value="Not Effectively">Not Effectively</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Divider />

                    {/* 4. Health and Nutrition Literacy */}
                    <Title level={4}>4. Health and Nutrition Literacy</Title>
                    <Text italic>Objective: Assess knowledge and application of healthy habits and nutritional practices.</Text>
                    <Divider />

                    {/* Basic (B) */}
                    <Title level={5}>Basic (B)</Title>
                    <Form.Item
                        label="Does the student understand the importance of eating a balanced diet?"
                        name="Does the student understand the importance of eating a balanced diet?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student identify unhealthy habits (e.g., excessive junk food consumption)?"
                        name="Can the student identify unhealthy habits (e.g., excessive junk food consumption)?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student recognize the importance of physical activity?"
                        name="Does the student recognize the importance of physical activity?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Progressing (P) */}
                    <Title level={5}>Progressing (P)</Title>
                    <Form.Item
                        label="Can the student explain the benefits of key nutrients (e.g., proteins, vitamins)?"
                        name="Can the student explain the benefits of key nutrients (e.g., proteins, vitamins)?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student practice basic hygiene regularly (e.g., handwashing, brushing teeth)?"
                        name="Does the student practice basic hygiene regularly (e.g., handwashing, brushing teeth)?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student design a simple healthy meal plan for a day?"
                        name="Can the student design a simple healthy meal plan for a day?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Advanced (A) */}
                    <Title level={5}>Advanced (A)</Title>
                    <Form.Item
                        label="Can the student critically analyze food labels and identify nutritional values?"
                        name="Can the student critically analyze food labels and identify nutritional values?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student actively incorporate healthy habits (e.g., yoga, exercise) into their routine?"
                        name="Does the student actively incorporate healthy habits (e.g., yoga, exercise) into their routine?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student educate peers about maintaining a healthy lifestyle?"
                        name="Can the student educate peers about maintaining a healthy lifestyle?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Divider />

                    {/* 5. Environmental Literacy */}
                    <Title level={4}>5. Environmental Literacy</Title>
                    <Text italic>Objective: Evaluate awareness of environmental issues and sustainable practices.</Text>
                    <Divider />

                    {/* Basic (B) */}
                    <Title level={5}>Basic (B)</Title>
                    <Form.Item
                        label="Can the student identify common environmental problems (e.g., pollution, deforestation)?"
                        name="Can the student identify common environmental problems (e.g., pollution, deforestation)?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student understand the importance of conserving water and energy?"
                        name="Does the student understand the importance of conserving water and energy?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student differentiate between biodegradable and non-biodegradable waste?"
                        name="Can the student differentiate between biodegradable and non-biodegradable waste?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Progressing (P) */}
                    <Title level={5}>Progressing (P)</Title>
                    <Form.Item
                        label="Does the student participate in basic environmental activities (e.g., planting trees, recycling)?"
                        name="Does the student participate in basic environmental activities (e.g., planting trees, recycling)?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student suggest ways to reduce waste in daily life?"
                        name="Can the student suggest ways to reduce waste in daily life?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student understand the impact of human activities on climate change?"
                        name="Does the student understand the impact of human activities on climate change?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Advanced (A) */}
                    <Title level={5}>Advanced (A)</Title>
                    <Form.Item
                        label="Can the student propose actionable solutions for environmental issues in their community?"
                        name="Can the student propose actionable solutions for environmental issues in their community?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student actively advocate for environmental conservation among peers?"
                        name="Does the student actively advocate for environmental conservation among peers?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student critically assess environmental policies and suggest improvements?"
                        name="Can the student critically assess environmental policies and suggest improvements?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Divider />

                    {/* 6. Critical Thinking */}
                    <Title level={4}>6. Critical Thinking</Title>
                    <Text italic>Objective: Assess the ability to analyze problems, evaluate evidence, and make logical decisions.</Text>
                    <Divider />

                    {/* Basic (B) */}
                    <Title level={5}>Basic (B)</Title>
                    <Form.Item
                        label="Can the student identify simple problems in daily life (e.g., missing the school bus)?"
                        name="Can the student identify simple problems in daily life (e.g., missing the school bus)?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label='Does the student ask "why" questions to seek clarification or deeper understanding?'
                        name='Does the student ask "why" questions to seek clarification or deeper understanding?'
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Frequently">Frequently</Radio>
                            <Radio value="Occasionally">Occasionally</Radio>
                            <Radio value="Rarely">Rarely</Radio>
                            <Radio value="Never">Never</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student list possible solutions to straightforward problems?"
                        name="Can the student list possible solutions to straightforward problems?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Progressing (P) */}
                    <Title level={5}>Progressing (P)</Title>
                    <Form.Item
                        label="Can the student analyze multiple sides of an argument before forming an opinion?"
                        name="Can the student analyze multiple sides of an argument before forming an opinion?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student use evidence to support their reasoning?"
                        name="Does the student use evidence to support their reasoning?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Always">Always</Radio>
                            <Radio value="Often">Often</Radio>
                            <Radio value="Sometimes">Sometimes</Radio>
                            <Radio value="Rarely">Rarely</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="How effectively does the student reflect on their past decisions to improve future outcomes?"
                        name="How effectively does the student reflect on their past decisions to improve future outcomes?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Very Effectively">Very Effectively</Radio>
                            <Radio value="Effectively">Effectively</Radio>
                            <Radio value="Somewhat Effectively">Somewhat Effectively</Radio>
                            <Radio value="Not Effectively">Not Effectively</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Advanced (A) */}
                    <Title level={5}>Advanced (A)</Title>
                    <Form.Item
                        label="Can the student tackle complex, real-world problems (e.g., designing a community project)?"
                        name="Can the student tackle complex, real-world problems (e.g., designing a community project)?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student demonstrate creative thinking while solving challenges?"
                        name="Does the student demonstrate creative thinking while solving challenges?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Always">Always</Radio>
                            <Radio value="Often">Often</Radio>
                            <Radio value="Sometimes">Sometimes</Radio>
                            <Radio value="Rarely">Rarely</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="How effectively does the student evaluate the long-term implications of their solutions?"
                        name="How effectively does the student evaluate the long-term implications of their solutions?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Very Effectively">Very Effectively</Radio>
                            <Radio value="Effectively">Effectively</Radio>
                            <Radio value="Somewhat Effectively">Somewhat Effectively</Radio>
                            <Radio value="Not Effectively">Not Effectively</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Divider />

                    {/* 7. Collaboration and Teamwork */}
                    <Title level={4}>7. Collaboration and Teamwork</Title>
                    <Text italic>Objective: Evaluate the ability to work effectively with others towards a common goal.</Text>
                    <Divider />

                    {/* Basic (B) */}
                    <Title level={5}>Basic (B)</Title>
                    <Form.Item
                        label="Does the student willingly participate in group activities?"
                        name="Does the student willingly participate in group activities?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student listen to and respect others' opinions?"
                        name="Can the student listen to and respect others' opinions?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Always">Always</Radio>
                            <Radio value="Often">Often</Radio>
                            <Radio value="Sometimes">Sometimes</Radio>
                            <Radio value="Rarely">Rarely</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student contribute to simple group tasks?"
                        name="Does the student contribute to simple group tasks?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Progressing (P) */}
                    <Title level={5}>Progressing (P)</Title>
                    <Form.Item
                        label="Can the student resolve conflicts within a team?"
                        name="Can the student resolve conflicts within a team?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student take responsibility for their assigned roles?"
                        name="Does the student take responsibility for their assigned roles?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Always">Always</Radio>
                            <Radio value="Often">Often</Radio>
                            <Radio value="Sometimes">Sometimes</Radio>
                            <Radio value="Rarely">Rarely</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="How effectively does the student motivate peers during collaborative tasks?"
                        name="How effectively does the student motivate peers during collaborative tasks?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Very Effectively">Very Effectively</Radio>
                            <Radio value="Effectively">Effectively</Radio>
                            <Radio value="Somewhat Effectively">Somewhat Effectively</Radio>
                            <Radio value="Not Effectively">Not Effectively</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Advanced (A) */}
                    <Title level={5}>Advanced (A)</Title>
                    <Form.Item
                        label="Can the student lead a team effectively?"
                        name="Can the student lead a team effectively?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student promote inclusivity and ensure equal participation?"
                        name="Does the student promote inclusivity and ensure equal participation?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Always">Always</Radio>
                            <Radio value="Often">Often</Radio>
                            <Radio value="Sometimes">Sometimes</Radio>
                            <Radio value="Rarely">Rarely</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="How well does the student align team efforts to achieve a shared objective?"
                        name="How well does the student align team efforts to achieve a shared objective?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Very Well">Very Well</Radio>
                            <Radio value="Well">Well</Radio>
                            <Radio value="Moderately Well">Moderately Well</Radio>
                            <Radio value="Poorly">Poorly</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Divider />

                    {/* 8. Scientific Temper and Evidence-Based Thinking */}
                    <Title level={4}>8. Scientific Temper and Evidence-Based Thinking</Title>
                    <Text italic>Objective: Assess curiosity, logical reasoning, and the use of evidence in problem-solving.</Text>
                    <Divider />

                    {/* Basic (B) */}
                    <Title level={5}>Basic (B)</Title>
                    <Form.Item
                        label="Does the student show curiosity by asking questions about natural phenomena?"
                        name="Does the student show curiosity by asking questions about natural phenomena?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Frequently">Frequently</Radio>
                            <Radio value="Occasionally">Occasionally</Radio>
                            <Radio value="Rarely">Rarely</Radio>
                            <Radio value="Never">Never</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student follow basic steps of an experiment and record observations?"
                        name="Can the student follow basic steps of an experiment and record observations?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student accept ideas without questioning their validity?"
                        name="Does the student accept ideas without questioning their validity?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Often">Often</Radio>
                            <Radio value="Sometimes">Sometimes</Radio>
                            <Radio value="Rarely">Rarely</Radio>
                            <Radio value="Never">Never</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Progressing (P) */}
                    <Title level={5}>Progressing (P)</Title>
                    <Form.Item
                        label="Can the student design simple experiments to test hypotheses?"
                        name="Can the student design simple experiments to test hypotheses?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Does the student rely on data and evidence to form conclusions?"
                        name="Does the student rely on data and evidence to form conclusions?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Always">Always</Radio>
                            <Radio value="Often">Often</Radio>
                            <Radio value="Sometimes">Sometimes</Radio>
                            <Radio value="Never">Never</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student identify errors or biases in experiments?"
                        name="Can the student identify errors or biases in experiments?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* Advanced (A) */}
                    <Title level={5}>Advanced (A)</Title>
                    <Form.Item
                        label="Can the student apply scientific principles to solve real-life problems?"
                        name="Can the student apply scientific principles to solve real-life problems?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="How effectively does the student present and defend their findings?"
                        name="How effectively does the student present and defend their findings?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Very Effectively">Very Effectively</Radio>
                            <Radio value="Effectively">Effectively</Radio>
                            <Radio value="Somewhat Effectively">Somewhat Effectively</Radio>
                            <Radio value="Not Effectively">Not Effectively</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="Can the student critically evaluate existing theories or ideas?"
                        name="Can the student critically evaluate existing theories or ideas?"
                        rules={[{ required: true, message: 'Please select an option.' }]}
                    >
                        <Radio.Group>
                            <Radio value="Yes">Yes</Radio>
                            <Radio value="No">No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Divider />

                    {/* Submit Button */}
                    <Form.Item>
                        <Button type="primary" className="bg-yellow-500 hover:bg-yellow-600 text-black" htmlType="submit" loading={isSubmitting}>
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
            ) : (
                <Text type="warning">
                    You can only submit this form once every 15 days. Please come back later.
                </Text>
            )}

            {/* Display Latest Submission */}
            {latestSubmission && isEligible && (
                <Card title="Your Latest Submission" style={{ marginTop: 24 }}>
                    {Object.entries(latestSubmission.qna).map(([question, answer], index) => (
                        <div key={index} style={{ marginBottom: 16 }}>
                            <Text strong>{question}</Text>
                            <br />
                            <Text>{answer}</Text>
                        </div>
                    ))}
                </Card>
            )}
        </Card>
    );

};

export default CompetencyProfile;
